import React from 'react';
import { get, map, fromPairs, cloneDeep, set, find, every, filter, includes, reduce, noop, isEqual } from 'lodash';

// import default view
import { matchCriteria as MatchCriteriaView } from './matchCriteria.view';

/**
 * @type {function}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @param {object} Component - A React component
 * @example
 * import { MatchCriteria } from 'modules/matchCriteria';
 *
 * // render
 * <MatchCriteria propName='propValue'/>
 */
export class MatchCriteria extends React.Component {
    constructor( props ) {
        super( props );

        // bind methods to instance
        this.handleSetChoice = this.handleSetChoice.bind( this );

        // filter those fields which are readonly
        const editableCriteria = ( filter( this.props.data.criteria, ( criteria ) => {
            return (
                true !== get( get( criteria, 'config', {} ), 'readonly', false )
            );
        } ) );

        // component state
        this.state = {
            fieldsStatus: fromPairs( map( editableCriteria, item => {
                return [ item.name, { result: null, value: null, } ];
            } ) ),
            criteriaStatus: {
                total: false,
                mandatory: false,
                optional: false,
            },
        };
    }

    UNSAFE_componentWillReceiveProps( nextProps ) {

        if( ! isEqual( this.props.data.values, nextProps.data.values ) ) {

            // filter those fields which are readonly
            const editableCriteria = ( filter( nextProps.data.criteria, ( criteria ) => {
                return (
                    true !== get( get( criteria, 'config', {} ), 'readonly', false )
                );
            } ) );

            this.setState( {
                fieldsStatus: fromPairs( map( editableCriteria, item => {
                    return [ item.name, { result: null, value: null, } ];
                } ) ),
                criteriaStatus: {
                    total: false,
                    mandatory: false,
                    optional: false,
                }
            } );
        }
    }

    /**
     * @desc Set `true` or `false` selection for a criteria field.
     */
    handleSetChoice( { field, result, value } = {} ) {
        log.debug( 'MatchCriteriaWidget.handleSetChoice()' );

        // get field status from the state
        const fieldsStatus = cloneDeep( this.state.fieldsStatus );
        const fieldStatus = fieldsStatus[ field.name ];

        // update `result` value
        set( fieldStatus, 'result', result );

        // set `value` if the result is `true`
        set( fieldStatus, 'value', ( true === result ? value : null ) );

        /*-------*/
        
        // mandatory and optional criteria fields excluding readonly
        const allCriteriaFields = get( this.props.data, 'criteria', [] );
        const criteriaFields = filter( allCriteriaFields, criteriaField => ! get( criteriaField.config, 'readonly', false ) );
        const mandatoryCriteria = get( this.props.data, 'mandatoryCriteria', {} );
        const optionalCriteria = get( this.props.data, 'optionalCriteria', {} );
        const minOptionalCriteriaMatch = get( this.props.data, 'minOptionalCriteriaMatch', null );
        const mandatoryFields = filter( criteriaFields, criteriaField => includes( mandatoryCriteria.ids, criteriaField.name ) );
        const optionalFields = filter( criteriaFields, criteriaField => includes( get( optionalCriteria, 'ids', [] ), criteriaField.name ) );
        
        // check if mandatory and optional fields are satisfied
        const isMandatorySectionSatisfied = every( mandatoryFields, mandatoryField => {
            return get( fieldsStatus, `${ mandatoryField.name }.result`, false );
        } );
        const isOptionalSectionSatisfied = minOptionalCriteriaMatch <= reduce( optionalFields, ( sum, optionalField ) => {
            return get( fieldsStatus, `${ optionalField.name }.result` ) ? sum + 1 : sum;
        }, 0 );

        /*-------*/

        // get save action (no-op if action is missing)
        const saveAction = find( get( this.props.data, 'actions', [] ), { name: 'save' } );

        // update state
        this.setState( {
            fieldsStatus,
            criteriaStatus: {
                total: isMandatorySectionSatisfied && isOptionalSectionSatisfied,
                mandatory: isMandatorySectionSatisfied,
                optional: isOptionalSectionSatisfied,
            }
        }, () => {
            this.props.saveCriteriaSelection( { saveAction: saveAction, fieldStatus: this.state.fieldsStatus } ); // update backend
            this.props.onMarkVerified( { isVerified: this.state.criteriaStatus.total } );
        } );
    }

    /*********************************************/

    render() {
        return (
            <MatchCriteriaView
                fieldsStatus={ this.state.fieldsStatus }
                criteriaStatus={ this.state.criteriaStatus }
                onSetChoice={ this.handleSetChoice }
                verified={ this.props.verified }
                { ...this.props.data }
                layout={ this.props.layout }
            />
        );
    }
}

// set display name
MatchCriteria.displayName = 'MatchCriteria';

// default props
MatchCriteria.defaultProps = {
    onMarkVerified: noop,
    verified: false,
    layout: {}
};

// prop types
MatchCriteria.propTypes = {};
